import CustomInput from '../../components/reusable/input';
import { FormSettingsParams } from '../../params/redux/form';
import { formSettingsThunk } from '../../redux/thunks/form';
import { getAllPipelinesThunk } from '../../redux/thunks/pipeline';
import PipelineApi from '../../apis/pipeline';
import { PipelineObject } from '../../params/redux/pipeline';
import { replaceToken } from '../../redux/slices';
import SelectPipeline from '../pipeline/select_pipeline';
import SelectStage from '../stage/select_stage';
import { settingSelectedPipeline } from '../../redux/slices/pipeline';
import { showError } from '../../redux/slices/error';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, FormControlLabel, Radio, RadioGroup, SelectChangeEvent, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

interface PipelineResponse extends Record<string, unknown> {
    success: 0 | 1;
    token: string;
    pipeline: PipelineObject
}

const SettingsComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
    const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
    const { pipelines, selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    // const { stages } = useSelector((state: RootState) => state.stage);
    const { selectedForm, loaders } = useSelector((state: RootState) => state.form);
    const [redirectOption, setRedirectOption] = useState<'message' | 'url'>(selectedForm?.redirect_url ? 'url' : 'message');
    const [redirectMessage, setRedirectMessage] = useState(selectedForm?.redirect_msg || '');
    const [redirectUrl, setRedirectUrl] = useState(selectedForm?.redirect_url || '');
    const { token } = useSelector((state: RootState) => state.app);


    useEffect(() => {
        const fetchAllPipelines = async () => {
            try {
                if (selectedForm) {
                    await dispatch(getAllPipelinesThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
                }
            } catch (e) {
                // Catch errors
            }
        };
        fetchAllPipelines();
    }, [dispatch]);

    useEffect(() => {
        if (selectedForm && selectedForm.pipeline_id && selectedForm.pipeline_id !== 0) {
            setSelectedPipelineId(selectedForm.pipeline_id);
            const fetchPipeline = async () => {
                try {
                    const response: PipelineResponse = await PipelineApi.getSinglePipelineDetail(token, selectedForm.pipeline_id as number) as PipelineResponse;
                    if (response.success) {
                        dispatch(replaceToken(response.token));
                        dispatch(settingSelectedPipeline(response?.pipeline));
                        setSelectedPipelineId(response?.pipeline?.id || 0);
                        setSelectedStageId(selectedForm.stage_id || null);
                    } else {
                        dispatch(showError({
                            title: 'Error',
                            description: response.msg
                        }));
                        setSelectedStageId(selectedForm.stage_id || null);
                    }
                } catch (e) {
                    console.error(e);
                    setSelectedStageId(selectedForm.stage_id || null);
                }
            };
            fetchPipeline();
        } else {
            setSelectedPipelineId(null);
            setSelectedStageId(null);
        }
    }, []);

    const handlePipelineChange = async (event: SelectChangeEvent<unknown>) => {
        event.preventDefault();
        const target = event.target as HTMLInputElement;
        if (target.value === 'choose') {
            setSelectedPipelineId(null);
            setSelectedStageId(null);
        } else {
            const pipelineId = parseInt(target.value as string, 10);
            try {
                const response: PipelineResponse = await PipelineApi.getSinglePipelineDetail(token, pipelineId) as PipelineResponse;
                if (response.success) {
                    dispatch(replaceToken(response.token));
                    dispatch(settingSelectedPipeline(response?.pipeline));
                    setSelectedPipelineId(response?.pipeline?.id || 0);
                    setSelectedStageId(null);
                } else {
                    dispatch(showError({
                        title: 'Error',
                        description: response.msg
                    }));
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const handleStageChange = (event: SelectChangeEvent<unknown>) => {
        const stageId = Number(event.target.value);
        setSelectedStageId(stageId);
    };

    const saveChangesHandler = async () => {
        if (redirectOption === 'url' && !redirectUrl.trim()) {
            toast.error('Redirect URL cannot be empty', {
                position: 'top-left',
                theme: 'dark'
            });
            return;
        }
        try {
            const data: FormSettingsParams = {
                form_id: selectedForm?.id || 0,
                pipeline_id: selectedPipelineId || 0,
                stage_id: selectedStageId || 0,
                status: selectedForm?.status as 'active' | 'inactive',
                client_id: selectedForm?.client_id || 0,
                redirect_url: redirectOption === 'message' ? '' : redirectUrl,
                redirect_msg: redirectOption === 'message' && redirectMessage === '' ? 'Form submitted successfully!' : redirectOption === 'message' ? redirectMessage : ''
            };
            await dispatch(formSettingsThunk(data)).unwrap();
            toast.success('Settings Updated Successfully', {
                position: 'top-left',
                theme: 'dark'
            });
        } catch (e) {
            //
        }
    };

    const disabled = loaders.isUpdating || loaders.isLoading || selectedForm?.status === 'deleted';

    return <Box minHeight="60vh" pt={2}>
        <Typography variant="subtitle1" color="textDisabled" gutterBottom>Select a pipeline and stage to send form submissions directly to your pipeline</Typography>
        <Box display="flex" gap={2} alignItems="center" my={2}>
            <Box width="100%">
                <SelectPipeline
                    label="Pipeline"
                    pipelines={pipelines}
                    onChange={handlePipelineChange}
                    value={selectedPipelineId || ''}
                    disabled={disabled}
                    enabledDefaultSelection={true}
                /></Box>
            <Box width="100%">
                <SelectStage
                    label="Stage"
                    stages={selectedPipeline?.pipeline_stages || []}
                    onChange={handleStageChange}
                    value={selectedStageId || ''}
                    disabled={disabled}
                />
            </Box>
        </Box>
        {selectedForm?.type === 'internal' && <Box>
            <Box mb={2}>
            <Typography variant="h4" color="textSecondary" gutterBottom>On Submission</Typography>
            <RadioGroup
                row
                value={redirectOption}
                onChange={(e) => setRedirectOption(e.target.value as 'url' | 'message')}
                sx={{ gap: 4, alignItems: 'center' }}
            >
                <FormControlLabel
                    value="message"
                    control={<Radio />}
                    label="Show Message"
                />
                <FormControlLabel
                    value="url"
                    control={<Radio />}
                    label="Redirect to URL"
                />
            </RadioGroup>
        </Box>
        {redirectOption === 'url'
            ? (
                <CustomInput
                    label="Redirect URL (Optional)"
                    value={redirectUrl}
                    placeholder="https://www.yourwebsite.com"
                    onChange={(e) => setRedirectUrl(e.target.value)}
                    fullWidth
                />
            )
            : (
                <CustomInput
                    label="Redirect Message (Optional)"
                    value={redirectMessage}
                    placeholder="Thank you for your submission!"
                    onChange={(e) => setRedirectMessage(e.target.value)}
                    fullWidth
                />
            )}
            </Box>}
        <Box display="flex" justifyContent="end" mt={2}>
            <Button variant="contained" onClick={saveChangesHandler}>Save Changes</Button>
        </Box>
    </Box>;
};

export default SettingsComponent;